<template>
  <div>
    <v-card flat>

      <!-- Toolbar -->
      <v-toolbar color="blue darken-3" dark dense flat>
        <v-icon class="mr-2">mdi-receipt-text-outline</v-icon>
        <v-toolbar-title class="font-weight-bold subtitle-1">
          Recibos generados manualmente
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn rounded small outlined class="mr-2" @click="$router.push('/recibosesam')">
          <v-icon small left>mdi-plus</v-icon> Generar Recibo
        </v-btn>
        <download-excel :data="data_download" :fields="json_fields" worksheet="Recibos" name="recibos.xls">
          <v-btn rounded small color="green lighten-1" :disabled="data_download.length === 0">
            <v-icon small left>mdi-microsoft-excel</v-icon> Descargar Excel
          </v-btn>
        </download-excel>
      </v-toolbar>

      <v-card-text class="px-4 pt-3 pb-0">

        <!-- Panel de filtros -->
        <v-card outlined class="mb-3">
          <v-card-title class="py-2 px-3 blue lighten-5 subtitle-2 blue--text text--darken-3">
            <v-icon small left color="blue darken-3">mdi-filter-outline</v-icon>
            Filtros de búsqueda
          </v-card-title>
          <v-card-text class="pt-3 pb-2">
            <v-row dense align="center">
              <v-col cols="12" sm="3" md="2">
                <v-text-field
                  v-model.trim="search_text"
                  label="Buscar por nombre"
                  outlined dense hide-details clearable
                  prepend-inner-icon="mdi-magnify"
                  autofocus
                />
              </v-col>
              <v-col cols="12" sm="3" md="2">
                <v-text-field
                  v-model.trim="busqueda.fecha_ini"
                  type="date"
                  label="Fecha inicio"
                  outlined dense hide-details
                  prepend-inner-icon="mdi-calendar-start"
                  :error="validation.hasError('busqueda.fecha_ini')"
                />
              </v-col>
              <v-col cols="12" sm="3" md="2">
                <v-text-field
                  v-model.trim="busqueda.fecha_fin"
                  type="date"
                  label="Fecha fin"
                  outlined dense hide-details
                  prepend-inner-icon="mdi-calendar-end"
                  :error="validation.hasError('busqueda.fecha_fin')"
                />
              </v-col>
              <v-col cols="auto">
                <v-btn color="blue darken-3" dark rounded small @click="cargarRecibos(null, 1)">
                  <v-icon left small>mdi-magnify</v-icon> Buscar
                </v-btn>
              </v-col>
              <v-col cols="auto">
                <v-btn small rounded color="success" @click="cargarRecibos(1, 1)">
                  <v-icon small left>mdi-check-all</v-icon> Ver Todos
                </v-btn>
              </v-col>
              <v-col cols="auto">
                <v-btn small rounded color="red darken-1" dark @click="cargarRecibos(1, 0)">
                  <v-icon small left>mdi-delete-outline</v-icon> Ver Eliminados
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

      </v-card-text>

      <!-- Tabla de recibos -->
      <v-card-text class="pt-0 px-4">
        <v-data-table
          :headers="headers"
          :items="recibos"
          :loading="loading"
          :search="search_text"
          :items-per-page="30"
          fixed-header
          height="calc(100vh - 46vh)"
          class="elevation-2"
          dense>

          <template v-slot:top>
            <v-card flat class="px-3 pt-2 pb-1">
              <span class="caption grey--text">
                <v-icon small class="mr-1">mdi-table</v-icon>
                <strong>{{ recibos.length }}</strong> recibos encontrados
              </span>
            </v-card>
            <v-divider></v-divider>
          </template>

          <template v-slot:[`item.nro`]="{ item }">
            <span class="font-weight-bold caption">00{{ item.nro }}</span>
          </template>

          <template v-slot:[`item.fecha_pago`]="{ item }">
            <span class="caption">{{ parseFecha(item.fecha_pago) }}</span>
          </template>

          <template v-slot:[`item.monto`]="{ item }">
            <span class="font-weight-bold green--text text--darken-2">{{ item.monto }}</span>
          </template>

          <template v-slot:[`item.acciones`]="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon small
                  v-bind="attrs" v-on="on"
                  :disabled="!item.archivo_id"
                  color="light-blue darken-1"
                  @click="abrirRecibo(item)">
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </template>
              Ver comprobante
            </v-tooltip>
          </template>

          <template v-slot:loading>
            <div class="text-center py-6">
              <v-progress-circular indeterminate color="blue darken-3" size="40"></v-progress-circular>
              <div class="caption mt-2 grey--text">Cargando recibos...</div>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-12 grey--text">
              <v-icon x-large color="grey lighten-2">mdi-table-search</v-icon>
              <div class="body-2 mt-3">Seleccione un rango de fechas y presione <strong>Buscar</strong></div>
            </div>
          </template>

        </v-data-table>
      </v-card-text>

    </v-card>

    <!-- Diálogo para ver comprobante PDF -->
    <v-dialog v-model="dialogRecibo" max-width="860" scrollable>
      <v-card>
        <v-toolbar color="blue darken-3" dark dense flat>
          <v-icon class="mr-2">mdi-file-pdf-box</v-icon>
          <v-toolbar-title class="subtitle-2">Comprobante de pago</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogRecibo = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0" style="min-height:560px">
          <v-progress-linear v-if="!base64pdf" indeterminate color="blue darken-3" />
          <iframe v-if="base64pdf" :src="base64pdf" width="100%" height="560" style="border:none"></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>

    <notifications group="auth" position="bottom right" />
    <Alertas ref="xxalertar" />
  </div>
</template>

<script>
import gql from 'graphql-tag';
import Alertas from '@/components/AAlertas.vue';
import { Validator } from 'simple-vue-validator';
import { error } from '@/components/Utils.js';
import xaxios from '@/xaxios';

export default {
  data() {
    return {
      file_name: 'Mis_Recibos_' + Date.now().toString(),
      busqueda: { fecha_ini: null, fecha_fin: null },
      search_text: null,
      activo: true,
      recibos: [],
      self: {},
      data_download: [],
      base64pdf: '',
      loading: false,
      dialogRecibo: false,

      headers: [
        { text: 'Nro', value: 'nro', sortable: true, width: 80 },
        { text: 'Estudiante', value: 'estudiante' },
        { text: 'Monto', value: 'monto', sortable: true, width: 100 },
        { text: 'Asesor', value: 'asesor' },
        { text: 'Emisor', value: 'emisor' },
        { text: 'Lugar', value: 'lugar' },
        { text: 'Fecha', value: 'fecha_pago', width: 110 },
        { text: 'Opciones', value: 'acciones', sortable: false, width: 80 },
      ],

      json_fields: {
        'Sede': 'Sede',
        'Lugar': 'Lugar',
        'Numero': 'Numero',
        'Numero Documento': 'Numero Documento',
        'Factura': 'Factura',
        'Estudiante': 'Estudiante',
        'Num Cuota': 'Num Cuota',
        'Monto': 'Monto',
        'Saldo': 'Saldo',
        'Forma Pago': 'Forma Pago',
        'Concepto': 'Concepto',
        'Celular': 'Celular',
        'Telefono': 'Telefono',
        'Direccion': 'Direccion',
        'Zona': 'Zona',
        'Fecha Pago': 'Fecha Pago',
        'Proximo Pago': 'Proximo Pago',
        'Asesor': 'Asesor',
        'Emisor': 'Emisor',
      },
    };
  },
  components: { Alertas },
  validators: {
    'busqueda.fecha_ini'(value) {
      return Validator.value(value).required();
    },
    'busqueda.fecha_fin'(value) {
      return Validator.value(value).required();
    },
  },

  watch: {
    '$store.state.CARGO': function () {
      this.cargarRecibos(null, 1);
    },
  },

  methods: {
    parseFecha(f) {
      var strs = f.split('T');
      var fs = strs[0].split('-');
      return fs[2] + '/' + fs[1] + '/' + fs[0];
    },
    cargarRecibos(todos, tipo) {
      if (!this.busqueda.fecha_ini || !this.busqueda.fecha_fin) {
        this.$notify(error('Faltan parametros', 'Complete los campos de fechas'));
        return;
      }
      this.loading = true;
      this.$refs.xxalertar.cargando();
      var vue = this;
      var idsede = this.$store.state.CARGO.idsede;
      var cargo = this.$store.state.CARGO.id;
      this.recibos = [];
      var filter = '';
      if (this.search_text) filter = this.search_text;
      else filter = null;
      var sql = ``;
      if (cargo == 1 || todos) {
        if (filter) {
          sql = `(search_text:"${filter}",sede_id:${idsede},fechas:${window.stringifyObject(this.busqueda)},tipo:${tipo})`;
        } else {
          sql = `(sede_id:${idsede},fechas:${window.stringifyObject(this.busqueda)},tipo:${tipo})`;
        }
      } else {
        var iduser = this.$store.state.DATA_USER.id;
        if (filter) {
          sql = `(search_text:"${filter}",sede_id:${idsede},fechas:${window.stringifyObject(this.busqueda)},usuario_id:${iduser},tipo:${tipo})`;
        } else {
          sql = `(sede_id:${idsede},fechas:${window.stringifyObject(this.busqueda)},usuario_id:${iduser},tipo:${tipo})`;
        }
      }
      sql = gql`query{ getRecibos${sql}{id,codigo,archivo_id,nro,lugar,num_doc,nro_factura,direccion,zona,estudiante,telefono,celular,concepto,forma_pago,monto,saldo,num_cuota,fecha_pago,proximo_pago,fecha_registro,asesor,emisor,recibo_fondo,sede_id,usuario_id,estado,sede{nombre},usuario{nombres,pri_apellido, usuario{firma} }} }`;

      this.$apollo
        .query({ query: sql })
        .then(({ data: { getRecibos } }) => {
          let res = getRecibos;
          res = res.sort((a, b) => b.nro - a.nro);
          this.recibos = res;
          this.formatDataDownload(this.recibos);
          this.loading = false;
          this.$refs.xxalertar.reset();
        })
        .catch((error) => {
          vue.activo = true;
          vue.msg = error;
          this.loading = false;
          vue.$refs.xxalertar.reset();
          this.$root.$notify.show('Error getRecibos', { color: 'red' });
        });
    },
    formatDataDownload(data) {
      this.data_download = data.map((recibo) => {
        let data = {};
        data.Sede = recibo.sede.nombre;
        data.Lugar = recibo.lugar ? recibo.lugar : '';
        data.Numero = recibo.nro ? recibo.nro : '';
        data['Numero Documento'] = recibo.num_doc ? recibo.num_doc : '';
        data.Factura = recibo.nro_factura ? recibo.nro_factura : '';
        data.Estudiante = recibo.estudiante ? recibo.estudiante : '';
        data['Num Cuota'] = recibo.num_cuota ? recibo.num_cuota : '';
        data.Monto = recibo.monto ? recibo.monto : '';
        data.Saldo = recibo.saldo ? recibo.saldo : '';
        data['Forma Pago'] = recibo.forma_pago ? recibo.forma_pago : '';
        data.Concepto = recibo.concepto ? recibo.concepto : '';
        data.Celular = recibo.celular ? recibo.celular : '';
        data.Telefono = recibo.telefono ? recibo.telefono : '';
        data.Direccion = recibo.direccion ? recibo.direccion : '';
        data.Zona = recibo.zona ? recibo.zona : '';
        data['Fecha Pago'] = recibo.fecha_pago ? recibo.fecha_pago : '';
        data['Proximo Pago'] = recibo.proximo_pago ? recibo.proximo_pago : '';
        data['Asesor'] = recibo.asesor ? recibo.asesor : '';
        data.Emisor = recibo.emisor ? recibo.emisor : '';
        return data;
      });
    },

    async abrirRecibo(recibin) {
      this.base64pdf = '';
      this.dialogRecibo = true;
      let config = {
        method: 'get',
        url: `${process.env.VUE_APP_SERVERFILES}${recibin.archivo_id}`,
        headers: { 'Content-Type': 'application/json' },
      };
      let res = await xaxios.request(config).then((response) => response.data).catch((error) => error);
      let b64 = `data:${res.mimetype};base64,${res.file64}`;
      this.base64pdf = b64;
    },

    /** ANULAR RECIBO */
    preAnularRecibo(recibo) {
      var vue = this;
      var opt = {
        verification: 'anular',
        verificationHelp: 'Escriba "[+:verification]" para anular este recibo.',
        okText: 'Anular Recibo',
        cancelText: 'cancelar',
        backdropClose: false,
        animation: 'bounce',
        loader: true,
        type: 'hard',
      };
      var message = {
        title: 'Confirmar anulacion',
        body: '<br> <b>NOTA:</b> El recibo sera eliminado.',
      };
      this.$dialog
        .confirm(message, opt)
        .then(function (dialog) {
          vue.anularRecibo(recibo, dialog);
        })
        .catch(function () {});
      return;
    },
    anularRecibo(recibo, dialog) {
      const vue = this;
      let sql = gql`mutation{ eliminarRecibo(input:{id:${recibo.id}}){ok errors{message}} }`;
      this.$apollo
        .mutate({ mutation: sql })
        .then(({ data: { eliminarRecibo } }) => {
          let datos = eliminarRecibo;
          if (datos.ok) {
            let rec = this.recibos.find((x) => x.id == recibo.id);
            if (rec) rec.nro = 'ELIMINADO';
          } else {
            let errores = JSON.stringify(eliminarRecibo.errors);
            let obj = JSON.parse(errores);
            let vals = '';
            obj.forEach(function (k) {
              vals += Object.values(k) + '-\n\t';
            });
            this.$notify(error('Error', vals));
          }
          vue.cargarRecibos(null, 1);
          dialog.close();
        })
        .catch((error) => {
          this.msg = error;
          window.console.error(error);
        });
    },
    /** FIN ANULAR */
  },
  mounted() {
    var date = new Date();
    var date2 = new Date();
    date.setDate(date.getDate() - 31);
    date2.setDate(date2.getDate() + 1);
    var d1 = date.getDate();
    var m1 = date.getMonth() + 1;
    var d2 = date2.getDate();
    var m2 = date2.getMonth() + 1;
    if (d1 < 10) d1 = '0' + d1;
    if (m1 < 10) m1 = '0' + m1;
    if (d2 < 10) d2 = '0' + d2;
    if (m2 < 10) m2 = '0' + m2;

    this.busqueda.fecha_ini = date.getFullYear() + '-' + m1 + '-' + d1;
    this.busqueda.fecha_fin = date2.getFullYear() + '-' + m2 + '-' + d2;

    this.cargarRecibos(null, 1);
  },
};
</script>
